// @react
import { useState, useContext, useEffect } from 'react'
// @types
import { ProfileFormValues } from 'modules/common/forms/types/profile'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
import {
  GetUserQuery,
  GetUserQueryResult,
  RemoveFileMutation,
  RemoveFileMutationVariables,
  UploadFileMutation,
  UploadFileMutationVariables,
  UserProfileUpdateMutation,
  UserProfileUpdateMutationVariables,
} from 'lib/graphql/graphqlTypes'
// @libraries
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import strings from 'lib/constants/strings'
import { vsprintf } from 'sprintf-js'
// @graphQL
import { ApolloQueryResult, useMutation } from '@apollo/client'
import {
  REMOVE_FILE,
  UPLOAD_FILE,
  USER_PROFILE_UPDATE,
} from 'graphql/mutations/user.mutation'
// @context
import { SnackbarContext } from 'modules/common/context/snackbar'
// @components
import { validationSchema } from 'modules/common/forms/validations/bio'

type PropsType = {
  data?: GetUserQueryResult['data']
  isLoading: boolean
  refetch: () => Promise<ApolloQueryResult<GetUserQuery>>
}
type Inputs = any

const useBioFormWrapper = <T extends Inputs>({
  data: userData,
  refetch,
}: PropsType) => {
  /****************************************
   * PROPS
   *****************************************/

  /****************************************
   * STATE
   *****************************************/

  const [imageFile, setImageFile] = useState<File[]>()

  /****************************************
   * HOOKS
   *****************************************/

  const snackbar = useContext(SnackbarContext)
  const formMethods = useForm<ProfileFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      profileImageUrl: '',
      firstName: '',
      lastName: '',
      bio: '',
    },
  })
  const { control, reset } = formMethods
  const user = userData?.user

  useEffect(() => {
    if (user && reset) {
      reset({
        publicProfile: user?.visibility.publicProfile || false,
        profileImageUrl: user?.profile?.profileImageUrl || '',
        firstName: user?.profile?.firstName || '',
        lastName: user?.profile?.lastName || '',
        bio: user?.profile?.bio || '',
      })
    }
  }, [reset, user])

  /****************************************
   * GQL HOOKS
   *****************************************/

  const [updateUserProfile] = useMutation<
    UserProfileUpdateMutation,
    UserProfileUpdateMutationVariables
  >(USER_PROFILE_UPDATE)

  const [uploadFile] = useMutation<
    UploadFileMutation,
    UploadFileMutationVariables
  >(UPLOAD_FILE)

  const [removeImage] = useMutation<
    RemoveFileMutation,
    RemoveFileMutationVariables
  >(REMOVE_FILE)

  /****************************************
   * FUNCTIONS
   *****************************************/

  const userProfileUpdateHandler = async (
    data: ProfileFormValues,
  ): Promise<boolean> => {
    // IF new file added to form

    if (imageFile?.length) {
      await uploadFile({ variables: { file: imageFile } })
    }

    // IF filelist is undefined BUT profile has image, then we've explicitly removed it, therefore delete mutation
    if (
      data.profileImageUrl === undefined &&
      userData?.user?.profile.profileImageUrl
    ) {
      console.log('removing image')
      await removeImage()
    }

    const { errors: mutationErrors } = await updateUserProfile({
      variables: {
        input: {
          firstName: data.firstName,
          lastName: data.lastName,
          bio: data.bio,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Bio',
        ]),
        show: true,
      })
      /*
      @todo leave this code for now, it will be useful if we modify mutatouib
      if (userData?.user && userData.user?.profile.profileImageUrl) {
       client.cache.modify({
          id: client.cache.identify(userData.user),
          fields: {
            profile(cachedData) {
              return {
                ...cachedData,
                bio: data.bio,
                profileImageUrl: isImage
                  ? `${data.profileImageUrl}?t=${now()}`
                  : null,
              }
            },
          },
        })
      }
      */

      // Refetch is warranted here, because we don't know the image file path before
      // datasource does seem to use one path for profile URL, but that could change, and then this would break
      // i.e. if profile image is null, we have no way to know new profile image URL before refetch (unless we send back in mutation response)
      await refetch()
      return true
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
    return false
  }

  /****************************************
   * RENDER
   *****************************************/
  return {
    formMethods,
    control,
    setImageFile,
    userProfileUpdateHandler,
  }
}

export default useBioFormWrapper
